/**
 * 猫アバターのパーツカタログ。
 *
 * ここが唯一の定義元。ビルダー UI の選択肢・SVG レンダラ・バリデーション schema は
 * すべてこのカタログから導出する (`../avatar/schema.ts`, `../avatar/render.ts`)。
 *
 * 保存されるのはパーツ ID だけ (色は 16 進値を保存しない)。
 * こうしておくとパレットを後から調整しても既存アバターの見た目を一括で更新できる。
 */

type ColorPair = { readonly label: string; readonly base: string; readonly shade: string };

/** 毛色。base = 明るい面、shade = 影・柄に使う色。 */
export const FUR_COLORS = {
  cream: { label: "クリーム", base: "#f4ddb9", shade: "#dcbc8e" },
  apricot: { label: "アプリコット", base: "#f3b97c", shade: "#d9975a" },
  ginger: { label: "茶トラ", base: "#e79a4f", shade: "#c67833" },
  chocolate: { label: "チョコレート", base: "#6a4530", shade: "#4d3121" },
  brown: { label: "こげ茶", base: "#8d6446", shade: "#6d4b33" },
  gray: { label: "グレー", base: "#bcc3cb", shade: "#98a1ac" },
  blue: { label: "ブルー", base: "#8fa3bd", shade: "#6b8199" },
  charcoal: { label: "チャコール", base: "#61646d", shade: "#484b53" },
  black: { label: "黒", base: "#3d3d45", shade: "#2b2b32" },
  silver: { label: "シルバー", base: "#e1e6ec", shade: "#c2cad4" },
  white: { label: "白", base: "#fbf7f1", shade: "#e4dbd0" },
} as const satisfies Record<string, ColorPair>;

/**
 * 瞳の色。実際の猫に現れる範囲 (銅 〜 琥珀 〜 金 〜 緑 〜 青) と、左右で違うオッドアイ。
 * `shade` は虹彩の外周 (limbal ring) に使い、平坦に見えないようにする。
 *
 * 隣り合う色まで並べると、アイコンの大きさ (24〜40px) では見分けが付かない。
 * 実際に区別できる幅だけを残している。
 */
export const EYE_COLORS = {
  copper: { label: "銅", base: "#b35f2e", shade: "#7d3d18" },
  amber: { label: "琥珀", base: "#e0a132", shade: "#a9741a" },
  gold: { label: "金", base: "#f2d24e", shade: "#bb9c28" },
  green: { label: "緑", base: "#6fae5a", shade: "#467033" },
  blue: { label: "青", base: "#5b9fd6", shade: "#2f6ea8" },
  odd: { label: "オッドアイ", base: "#5b9fd6", shade: "#e0a132" },
} as const satisfies Record<string, ColorPair>;

/**
 * 背景色。none は透明。
 * 薄すぎると付けたことが分からないので、猫の毛色と並べても背景と分かる濃さにする。
 */
export const BACKGROUND_COLORS = {
  none: { label: "なし", base: "transparent", shade: "transparent" },
  mint: { label: "ミント", base: "#9fdcc0", shade: "#74c19d" },
  peach: { label: "ピーチ", base: "#f9b9a5", shade: "#ef9a7f" },
  sky: { label: "スカイ", base: "#a5cdee", shade: "#7cb0de" },
  lavender: { label: "ラベンダー", base: "#c6b6e8", shade: "#a893d6" },
  sand: { label: "サンド", base: "#e5cfa2", shade: "#d0b57b" },
  slate: { label: "スレート", base: "#b4c0ce", shade: "#94a3b5" },
} as const satisfies Record<string, ColorPair>;

const labeled = <const T extends readonly { id: string; label: string }[]>(items: T) => items;

/** 顔の輪郭。 */
export const FACE_SHAPES = labeled([
  { id: "round", label: "丸顔" },
  { id: "slim", label: "すっきり" },
  { id: "chubby", label: "ぽっちゃり" },
]);

/** 毛の柄。 */
export const PATTERNS = labeled([
  { id: "solid", label: "単色" },
  { id: "tabby", label: "トラ" },
  { id: "tuxedo", label: "ハチワレ" },
  { id: "calico", label: "三毛" },
  { id: "point", label: "ポイント" },
  { id: "spotted", label: "まだら" },
]);

/** 耳。 */
export const EARS = labeled([
  { id: "pointed", label: "とんがり" },
  { id: "round", label: "まる耳" },
  { id: "folded", label: "垂れ耳" },
]);

/** 目の形。 */
export const EYES = labeled([
  { id: "round", label: "まるい" },
  { id: "almond", label: "アーモンド" },
  { id: "sleepy", label: "ねむそう" },
  { id: "wink", label: "ウインク" },
  { id: "closed", label: "にっこり" },
  { id: "starry", label: "きらきら" },
]);

/** 口もと。 */
export const MOUTHS = labeled([
  { id: "smile", label: "にっこり" },
  { id: "neutral", label: "すまし顔" },
  { id: "open", label: "あーん" },
  { id: "tongue", label: "ぺろっ" },
  { id: "grumpy", label: "ぶすっ" },
]);

/**
 * ひげ。ひげのない猫はいないので「なし」は置かない。
 * 長さではなく垂れ方で分ける (長さの違いはアイコンの大きさでは読めない)。
 */
export const WHISKERS = labeled([
  { id: "straight", label: "まっすぐ" },
  { id: "gentle", label: "ゆるやか" },
  { id: "droopy", label: "垂れひげ" },
]);

/** アクセサリ。 */
export const ACCESSORIES = labeled([
  { id: "none", label: "なし" },
  { id: "scarf", label: "マフラー" },
  { id: "collar-bell", label: "鈴付き首輪" },
  { id: "glasses", label: "めがね" },
  { id: "headphones", label: "ヘッドホン" },
  { id: "flower", label: "お花" },
  { id: "cap", label: "キャップ" },
  { id: "beanie", label: "ニット帽" },
  { id: "ribbon", label: "リボン" },
]);

const ids = <const T extends readonly { id: string }[]>(items: T) =>
  items.map((i) => i.id) as unknown as { [K in keyof T]: T[K]["id"] };

export const FACE_SHAPE_IDS = ids(FACE_SHAPES);
export const PATTERN_IDS = ids(PATTERNS);
export const EAR_IDS = ids(EARS);
export const EYE_IDS = ids(EYES);
export const MOUTH_IDS = ids(MOUTHS);
export const WHISKER_IDS = ids(WHISKERS);
export const ACCESSORY_IDS = ids(ACCESSORIES);

export const FUR_COLOR_IDS = Object.keys(FUR_COLORS) as (keyof typeof FUR_COLORS)[];
export const EYE_COLOR_IDS = Object.keys(EYE_COLORS) as (keyof typeof EYE_COLORS)[];
export const BACKGROUND_IDS = Object.keys(BACKGROUND_COLORS) as (keyof typeof BACKGROUND_COLORS)[];

export type FurColorId = keyof typeof FUR_COLORS;
export type EyeColorId = keyof typeof EYE_COLORS;
export type BackgroundId = keyof typeof BACKGROUND_COLORS;
export type FaceShapeId = (typeof FACE_SHAPE_IDS)[number];
export type PatternId = (typeof PATTERN_IDS)[number];
export type EarId = (typeof EAR_IDS)[number];
export type EyeId = (typeof EYE_IDS)[number];
export type MouthId = (typeof MOUTH_IDS)[number];
export type WhiskerId = (typeof WHISKER_IDS)[number];
export type AccessoryId = (typeof ACCESSORY_IDS)[number];
